<template>
    <div class="payment-card" :class="{'mobile': getMobileAppState}" @click="emit('cardClicked')">
        <!-- Image -->
        <img :key="getAppThemeMode" class="payment-image" :src="imageHandler(paymentParse.imageUrl)" @error="errorImage"/>
        <div class="payment-info">
            <div class="group-name">{{ paymentParse.operatorGroupName }}</div>
            <div class="parent-name">{{ paymentParse.parentName }}</div>
            <div v-if="paymentParse.showDescription" class="description">{{ paymentParse.description }}</div>
        </div>
    </div>
</template>

<script setup>
/* Vue & Assets */
import { computed } from 'vue'
import { categoryIconMapper } from '@/assets/scripts/mappers/payments'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* mapGetters */
const { getMobileAppState, getAppThemeMode } = mapGetters()

/* Emmitters */
const emit = defineEmits(['cardClicked'])

/* Props
@paymentInfo {Object} - Operation info to display on card
@paymentsOperatorsList {Array} - Payments Operations List
*/
const props = defineProps({
    paymentInfo: Object,
    paymentsOperatorsList: Array
})

const paymentParse = computed(() => {
    let info

    props.paymentsOperatorsList.find(operator => {
        if (operator.id && operator.id === props.paymentInfo.operation.operatorId) {
            info = operator
        } else {
            operator.children.find(operatorChildren => {
                if (operatorChildren.id && operatorChildren.id === props.paymentInfo.operation.operatorId) {
                    info = {
                        categoryId: operator.categoryId,
                        ...operator,
                        ...operatorChildren
                    }
                } else {
                    operatorChildren.children.find(children => {
                        if (children.id && children.id === props.paymentInfo.operation.operatorId) {
                            info = {
                                categoryId: operator.categoryId,
                                ...operatorChildren,
                                ...children,
                                showDescription: operatorChildren.children.length > 1
                            }
                        }
                    })
                }
            })
        }
    })
    return info
})

const imageHandler = img => {
    return img || `./images${categoryIconMapper(paymentParse.value.categoryId, getAppThemeMode.value === 'dark')}.svg`
}

const errorImage = event => {
    event.target.src = `./images${categoryIconMapper(paymentParse.value.categoryId, getAppThemeMode.value === 'dark')}.svg`
}
</script>

<style lang="scss" scoped>
.payment-card {
    width: calc((100% - 24px) / 4);
    height: 71px;

    display: flex;
    align-items: center;

    flex-shrink: 0;
    gap: 12px;

    padding: 8px 16px;

    border-radius: 16px;
    background-color: var(--secondary-light-4);
    border: 1px solid var(--secondary-light-1);

    cursor: pointer;

    &:hover {
        background-color: var(--secondary-light-2);
    }

    &:active {
        background-color: var(--secondary-light-1);
    }

    .payment-image {
        width: 32px;
        height: 32px;

        display: flex;
        flex-shrink: 0;
        margin-right: 8px;

        border-radius: 50%;
        border: 1px solid var(--secondary-light-2);
        background-color: var(--secondary-light-2);
        object-fit: scale-down;
    }

    .payment-info {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        gap: 2px;

        .group-name {
            @include H7-Regular;
            line-height: 10px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--secondary-dark-1);
        }

        .parent-name {
            @include H7-Medium;
            line-height: 16px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--secondary-dark-3);
        }

        .description {
            @include H8-Regular;
            line-height: 13px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--secondary-dark-3);
        }
    }

    &.mobile {
        width: calc(100% - 4px);
    }
}
</style>
